(() => {
  const CONFIGURATOR_SELECTOR = '[data-pergola-configurator]';
  // TEMPORARY MANUAL-ONLY MODE
  // Electric / remote / LED variants remain in Shopify Admin, but are hidden
  // from this storefront configurator until this mode is reversed.
  const STEP_ORDER = ['finish', 'size', 'legs', 'installation'];
  const PREVIEW_STEP_ORDER = ['finish', 'size', 'legs'];

  class PergolaConfigurator {
    constructor(root) {
      if (!root || root.dataset.pergolaInitialised === 'true') return;

      this.root = root;
      this.root.dataset.pergolaInitialised = 'true';

      this.nativePicker = this.root.querySelector(
        '[data-native-variant-picker]'
      );

      this.variantDataElement = this.root.querySelector(
        '[data-pergola-variants]'
      );

      this.optionNamesElement = this.root.querySelector(
        '[data-pergola-option-names]'
      );

      this.statusElement = this.root.querySelector(
        '[data-configurator-status]'
      );

      this.currencyCode =
        this.root.dataset.currencyCode ||
        window.Shopify?.currency?.active ||
        'GBP';

      this.selection = {
        finish: null,
        size: null,
        control: 'Manual',
        legs: null,
        installation: null
      };

      this.init();
    }

    init() {
      try {
        this.rawVariants = JSON.parse(
          this.variantDataElement?.textContent || '[]'
        );

        this.optionNames = JSON.parse(
          this.optionNamesElement?.textContent || '[]'
        );
      } catch (error) {
        console.error('Pergola configurator data could not be read.', error);
        return;
      }

      if (!Array.isArray(this.rawVariants) || !this.rawVariants.length) {
        console.warn('Pergola configurator: no Shopify variants found.');
        return;
      }

      if (!Array.isArray(this.optionNames) || !this.optionNames.length) {
        console.warn('Pergola configurator: no product options found.');
        return;
      }

      this.findProductOptionIndexes();
      this.prepareVariants();

      if (!this.variants.length) {
        console.warn(
          'Pergola configurator: no usable configuration variants found.'
        );
        return;
      }

      this.setInitialSelection();
      this.bindEvents();
      this.normaliseSelection();
      this.render();

      this.root.classList.add('is-ready');
    }

    findProductOptionIndexes() {
      this.finishOptionIndex = this.optionNames.findIndex((name) =>
        /colou?r|finish/i.test(String(name))
      );

      if (this.finishOptionIndex === -1) {
        this.finishOptionIndex = 0;
      }

      this.configurationOptionIndex = this.optionNames.findIndex(
        (name, index) =>
          index !== this.finishOptionIndex &&
          /configuration|config|size|dimensions|pergola|model/i.test(
            String(name)
          )
      );

      if (this.configurationOptionIndex === -1) {
        this.configurationOptionIndex = this.optionNames.findIndex(
          (_name, index) => index !== this.finishOptionIndex
        );
      }

      this.installationOptionIndex = this.optionNames.findIndex(
        (name, index) =>
          index !== this.finishOptionIndex &&
          index !== this.configurationOptionIndex &&
          /installation|install|fitting|fit/i.test(String(name))
      );

      if (this.installationOptionIndex === -1) {
        this.installationOptionIndex = this.optionNames.findIndex(
          (_name, index) =>
            index !== this.finishOptionIndex &&
            index !== this.configurationOptionIndex
        );
      }
    }

    prepareVariants() {
      // Keep all electric / remote / motor / LED variants intact in Shopify,
      // but exclude them from the custom configurator for manual-only mode.
      const manualVariants = this.rawVariants.filter((variant) => {
        const options = Array.isArray(variant.options)
          ? variant.options
          : [];

        const rawConfiguration =
          options[this.configurationOptionIndex] || variant.title || '';

        return !/electric|remote|motor|led/i.test(
          String(rawConfiguration)
        );
      });

      const anyAvailableVariant = manualVariants.some(
        (variant) => variant.available
      );

      this.variants = manualVariants
        .map((variant) => {
          const options = Array.isArray(variant.options)
            ? variant.options
            : [];

          const finish = options[this.finishOptionIndex];
          const rawConfiguration =
            options[this.configurationOptionIndex] || variant.title || '';
          const installation =
            this.installationOptionIndex > -1
              ? options[this.installationOptionIndex]
              : 'No Installation';

          if (!finish || !rawConfiguration || !installation) return null;

          const configuration = this.parseConfiguration(rawConfiguration);

          return {
            id: Number(variant.id),
            available: Boolean(variant.available),
            selectable: anyAvailableVariant
              ? Boolean(variant.available)
              : true,
            price: variant.price,
            compareAtPrice: variant.compare_at_price,
            title: variant.title,
            mediaId:
              variant.featured_media?.id ||
              variant.featured_image?.id ||
              null,
            imageUrl:
              variant.featured_image?.src ||
              variant.featured_media?.preview_image?.src ||
              '',
            options,
            finish: String(finish).trim(),
            rawConfiguration: String(rawConfiguration).trim(),
            size: configuration.size,
            control: configuration.control,
            legs: configuration.legs,
            installation: String(installation).trim()
          };
        })
        .filter(Boolean);
    }

    parseConfiguration(value) {
      const text = String(value)
        .replace(/×/g, 'x')
        .replace(/\s+/g, ' ')
        .trim();

      const sizeMatch = text.match(
        /(\d+(?:\.\d+)?)\s*m?\s*x\s*(\d+(?:\.\d+)?)\s*m?/i
      );

      let size = text;

      if (sizeMatch) {
        size = `${sizeMatch[1]}m × ${sizeMatch[2]}m`;
      }

      let control = 'Manual';

      if (/electric|remote|motor|led/i.test(text)) {
        control = 'Electric with LED';
      } else if (/manual|hand/i.test(text)) {
        control = 'Manual';
      }

      let legs = 'Standard frame';

      if (/\b6\s*[- ]?\s*leg/i.test(text)) {
        legs = '6 legs';
      } else if (/\b4\s*[- ]?\s*leg/i.test(text)) {
        legs = '4 legs';
      }

      return {
        size,
        control,
        legs
      };
    }

    setInitialSelection() {
      this.selection = {
        finish: null,
        size: null,
        control: 'Manual',
        legs: null,
        installation: null
      };
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const button = event.target.closest(
          '[data-configurator-option]'
        );

        if (!button || button.disabled) return;

        const step = button.dataset.configuratorStep;
        const value = button.dataset.configuratorValue;

        if (!STEP_ORDER.includes(step) || !value) return;

        this.selectOption(step, value);
      });
    }

    selectOption(step, value) {
      const stepIndex = STEP_ORDER.indexOf(step);

      this.selection[step] = value;

      STEP_ORDER.slice(stepIndex + 1).forEach((laterStep) => {
        this.selection[laterStep] = null;
      });

      this.normaliseSelection();
      this.render();
      this.applySelectedVariant();
      this.scrollToNextStep(step);
    }

    scrollToNextStep(currentStep) {
      const isMobile = window.matchMedia(
        '(max-width: 749px)'
      ).matches;

      if (isMobile) return;

      const currentStepIndex = STEP_ORDER.indexOf(currentStep);
      const nextStep = STEP_ORDER[currentStepIndex + 1];

      const targetElement = nextStep
        ? this.root.querySelector(
            `[data-configurator-step="${nextStep}"]`
          )
        : this.root.querySelector(
            '.pergola-configurator__summary'
          );

      if (!targetElement) return;

      const getHeaderOffset = () => {
        const header =
          document.querySelector('sticky-header') ||
          document.querySelector('.shopify-section-header-sticky') ||
          document.querySelector('.header-wrapper') ||
          document.querySelector('header');

        const headerHeight = header
          ? header.getBoundingClientRect().height
          : 72;

        return Math.max(84, Math.round(headerHeight + 32));
      };

      const scrollToTarget = (behavior) => {
        const reducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

        const targetRect = targetElement.getBoundingClientRect();
        const targetTop =
          window.pageYOffset + targetRect.top - getHeaderOffset();

        window.scrollTo({
          top: Math.max(0, targetTop),
          left: 0,
          behavior: reducedMotion ? 'auto' : behavior
        });
      };

      /*
        The installation step is directly after the frame layout step.
        Scroll to the actual step element, not the review summary or image area.
        A second quiet correction handles layout shifts from price, image and app widgets.
      */

      if (nextStep === 'installation') {
        window.setTimeout(() => scrollToTarget('smooth'), 120);
        window.setTimeout(() => scrollToTarget('auto'), 520);
        return;
      }

      window.setTimeout(() => scrollToTarget('smooth'), 220);
    }

    normaliseSelection() {
      STEP_ORDER.forEach((step, index) => {
        const values = this.getValuesForStep(step);

        const selectableValues = values.filter((value) =>
          this.isValueSelectable(step, value)
        );

        if (
          this.selection[step] &&
          !selectableValues.includes(this.selection[step])
        ) {
          this.selection[step] = null;

          STEP_ORDER.slice(index + 1).forEach((laterStep) => {
            this.selection[laterStep] = null;
          });
        }
      });
    }

    getValuesForStep(step) {
      const values = this.variants
        .map((variant) => variant[step])
        .filter(Boolean);

      return [...new Set(values)];
    }

    getPriorSteps(step) {
      const index = STEP_ORDER.indexOf(step);
      return STEP_ORDER.slice(0, index);
    }

    isValueSelectable(step, value) {
      const priorSteps = this.getPriorSteps(step);

      return this.variants.some((variant) => {
        if (!variant.selectable) return false;
        if (variant[step] !== value) return false;

        return priorSteps.every((priorStep) => {
          const selectedValue = this.selection[priorStep];

          if (!selectedValue) return true;

          return variant[priorStep] === selectedValue;
        });
      });
    }

    render() {
      STEP_ORDER.forEach((step) => {
        this.renderOptions(step);
      });

      this.updateSummary();
      this.updateStepStates();
      this.updateStatus();
    }

    renderOptions(step) {
      const container = this.root.querySelector(
        `[data-configurator-options="${step}"]`
      );

      if (!container) return;

      container.innerHTML = '';

      const values = this.getValuesForStep(step);

      values.forEach((value) => {
        const selectable = this.isValueSelectable(step, value);
        const selected = this.selection[step] === value;

        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'pergola-configurator__option';
        button.dataset.configuratorOption = '';
        button.dataset.configuratorStep = step;
        button.dataset.configuratorValue = value;
        button.disabled = !selectable;
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');

        if (selected) {
          button.classList.add('is-selected');
        }

        if (!selectable) {
          button.classList.add('is-unavailable');
        }

        const content = document.createElement('span');
        content.className =
          'pergola-configurator__option-content';

        if (step === 'finish') {
          const swatch = document.createElement('span');
          swatch.className = 'pergola-configurator__swatch';
          swatch.setAttribute('aria-hidden', 'true');
          swatch.style.setProperty(
            '--swatch-colour',
            this.getSwatchColour(value)
          );

          content.appendChild(swatch);
        }

        const copy = document.createElement('span');
        copy.className = 'pergola-configurator__option-copy';

        const title = document.createElement('span');
        title.className = 'pergola-configurator__option-title';
        title.textContent = value;

        const description = document.createElement('span');
        description.className =
          'pergola-configurator__option-description';
        description.textContent = this.getOptionDescription(
          step,
          value
        );

        copy.appendChild(title);
        copy.appendChild(description);
        content.appendChild(copy);

        const check = document.createElement('span');
        check.className = 'pergola-configurator__check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';

        content.appendChild(check);
        button.appendChild(content);
        container.appendChild(button);
      });
    }

    getOptionDescription(step, value) {
      if (step === 'finish') {
        return 'Powder-coated aluminium finish';
      }

      if (step === 'size') {
        return '';
      }

      if (step === 'control') {
        if (/electric/i.test(value)) {
          return 'Remote controlled roof with integrated LED';
        }

        return 'Smooth manual louvre operation';
      }

      if (step === 'legs') {
        if (/6/i.test(value)) {
          return 'Additional support for larger structures';
        }

        if (/4/i.test(value)) {
          return 'Open four-post frame layout';
        }

        return 'Frame layout supplied for this configuration';
      }

      if (step === 'installation') {
        if (/include|yes|with/i.test(value)) {
          return 'Hampton Row installation included in the selected price';
        }

        return 'Pergola supplied without installation';
      }

      return '';
    }

    ensureInstallationCostRow() {
      if (
        this.root.querySelector(
          '[data-configurator-summary="installationCost"]'
        )
      ) {
        return;
      }

      const priceRow = this.root.querySelector(
        '.pergola-configurator__summary-row--price'
      );

      if (!priceRow || !priceRow.parentElement) return;

      const row = document.createElement('div');
      row.className =
        'pergola-configurator__summary-row pergola-configurator__summary-row--installation-cost';
      row.setAttribute(
        'data-configurator-generated-installation-cost',
        ''
      );

      const label = document.createElement('dt');
      label.textContent = 'Installation cost';

      const value = document.createElement('dd');
      value.dataset.configuratorSummary = 'installationCost';
      value.textContent = 'Not selected';

      row.appendChild(label);
      row.appendChild(value);

      priceRow.parentElement.insertBefore(row, priceRow);
    }

    getBaseVariantForCurrentSelection() {
      if (!this.hasPreviewSelectionComplete()) {
        return null;
      }

      const matchingVariants = this.variants.filter(
        (variant) =>
          variant.finish === this.selection.finish &&
          variant.size === this.selection.size &&
          variant.control === this.selection.control &&
          variant.legs === this.selection.legs
      );

      if (!matchingVariants.length) {
        return null;
      }

      return (
        matchingVariants.find((variant) =>
          this.isNoInstallationValue(variant.installation)
        ) || matchingVariants[0]
      );
    }

    updateInstallationCostSummary() {
      const element = this.root.querySelector(
        '[data-configurator-summary="installationCost"]'
      );

      if (!element) return;

      if (!this.hasPreviewSelectionComplete()) {
        element.textContent = 'Select pergola first';
        return;
      }

      if (!this.selection.installation) {
        element.textContent = 'Select installation option';
        return;
      }

      const selectedVariant = this.getMatchingVariant();
      const baseVariant = this.getBaseVariantForCurrentSelection();

      if (!selectedVariant || !baseVariant) {
        element.textContent = 'Unavailable';
        return;
      }

      const installationCost = Math.max(
        0,
        Number(selectedVariant.price) - Number(baseVariant.price)
      );

      if (!Number.isFinite(installationCost)) {
        element.textContent = 'Unavailable';
        return;
      }

      element.textContent = installationCost > 0
        ? `+${this.formatMoney(installationCost)}`
        : this.formatMoney(0);
    }

    getSwatchColour(value) {
      const colour = String(value).toLowerCase();

      if (colour.includes('anthracite')) {
        return '#424446';
      }

      if (
        colour.includes('light grey') ||
        colour.includes('light gray')
      ) {
        return '#c8c9c6';
      }

      if (
        colour === 'grey' ||
        colour === 'gray' ||
        colour.includes('silver')
      ) {
        return '#8d9090';
      }

      if (colour.includes('white')) {
        return '#f7f6f1';
      }

      if (colour.includes('black')) {
        return '#151515';
      }

      if (
        colour.includes('wood') ||
        colour.includes('timber') ||
        colour.includes('oak')
      ) {
        return 'linear-gradient(135deg, #9f7651 0%, #c29a70 48%, #815d3e 100%)';
      }

      return '#d8d7d2';
    }

    updateSummary() {
      STEP_ORDER.forEach((step) => {
        const element = this.root.querySelector(
          `[data-configurator-summary="${step}"]`
        );

        if (!element) return;

        element.textContent =
          this.selection[step] || 'Not selected';
      });

      this.ensureInstallationCostRow();
      this.updateInstallationCostSummary();

      const priceElement = this.root.querySelector(
        '[data-configurator-summary="price"]'
      );

      if (!priceElement) return;

      const variant = this.getMatchingVariant();

      priceElement.textContent = variant
        ? this.formatMoney(variant.price)
        : 'Select all options';
    }

    updateStepStates() {
      const firstIncompleteStep =
        STEP_ORDER.find((step) => !this.selection[step]) ||
        STEP_ORDER[STEP_ORDER.length - 1];

      STEP_ORDER.forEach((step) => {
        const stepElement = this.root.querySelector(
          `[data-configurator-step="${step}"]`
        );

        const progressElement = this.root.querySelector(
          `[data-progress-step="${step}"]`
        );

        const complete = Boolean(this.selection[step]);
        const active = step === firstIncompleteStep;

        [stepElement, progressElement].forEach((element) => {
          if (!element) return;

          element.classList.toggle('is-complete', complete);
          element.classList.toggle('is-active', active);
        });
      });
    }

    getMatchingVariant() {
      if (
        STEP_ORDER.some((step) => !this.selection[step])
      ) {
        return null;
      }

      return this.variants.find(
        (variant) =>
          variant.finish === this.selection.finish &&
          variant.size === this.selection.size &&
          variant.control === this.selection.control &&
          variant.legs === this.selection.legs &&
          variant.installation === this.selection.installation
      );
    }

    hasPreviewSelectionComplete() {
      return PREVIEW_STEP_ORDER.every(
        (step) => this.selection[step]
      );
    }

    isNoInstallationValue(value) {
      return /no|without/i.test(String(value || ''));
    }

    getPreviewVariant() {
      if (!this.hasPreviewSelectionComplete()) {
        return null;
      }

      const matchingVariants = this.variants.filter(
        (variant) =>
          variant.finish === this.selection.finish &&
          variant.size === this.selection.size &&
          variant.control === this.selection.control &&
          variant.legs === this.selection.legs
      );

      if (!matchingVariants.length) {
        return null;
      }

      return (
        matchingVariants.find(
          (variant) =>
            variant.selectable &&
            variant.imageUrl &&
            this.isNoInstallationValue(variant.installation)
        ) ||
        matchingVariants.find(
          (variant) => variant.selectable && variant.imageUrl
        ) ||
        matchingVariants.find(
          (variant) =>
            variant.imageUrl &&
            this.isNoInstallationValue(variant.installation)
        ) ||
        matchingVariants.find((variant) => variant.imageUrl) ||
        matchingVariants[0]
      );
    }

    applyPreviewMedia(variant) {
      if (!variant || !variant.id) return;

      this.updateMobilePreview(variant);

      const isMobile = window.matchMedia(
        '(max-width: 749px)'
      ).matches;

      if (isMobile) return;

      if (this.lastPreviewVariantId === variant.id) {
        return;
      }

      this.lastPreviewVariantId = variant.id;
      this.updateDesktopGalleryPreview(variant);
    }

    updateDesktopGalleryPreview(variant) {
      if (!variant) return;

      const productInfo = this.root.closest('product-info');

      if (!productInfo) return;

      const mediaGallery = productInfo.querySelector(
        'media-gallery'
      );

      if (!mediaGallery) return;

      const targetMedia = this.getDesktopGalleryMediaElement(
        mediaGallery,
        variant
      );

      if (!targetMedia) return;

      const mediaDataId = targetMedia.dataset.mediaId;

      if (!mediaDataId) return;

      if (typeof mediaGallery.setActiveMedia === 'function') {
        mediaGallery.setActiveMedia(mediaDataId, true);
        return;
      }

      const escapedMediaId = this.escapeSelector(mediaDataId);
      const thumbnail = mediaGallery.querySelector(
        `[data-target="${escapedMediaId}"], [aria-controls="${escapedMediaId}"], [aria-controls$="${escapedMediaId}"]`
      );

      if (thumbnail) {
        thumbnail.click();
      }
    }

    getDesktopGalleryMediaElement(mediaGallery, variant) {
      const mediaId = variant.mediaId
        ? String(variant.mediaId)
        : '';

      if (mediaId) {
        const directMatch = mediaGallery.querySelector(
          `[data-media-id$="-${this.escapeSelector(mediaId)}"]`
        );

        if (directMatch) {
          return directMatch;
        }

        const exactMatch = mediaGallery.querySelector(
          `[data-media-id="${this.escapeSelector(mediaId)}"]`
        );

        if (exactMatch) {
          return exactMatch;
        }
      }

      if (!variant.imageUrl) {
        return null;
      }

      const cleanVariantImageUrl = String(variant.imageUrl).split('?')[0];

      const matchingImage = Array.from(
        mediaGallery.querySelectorAll('img')
      ).find((image) => {
        const cleanImageUrl = String(
          image.currentSrc || image.src || ''
        ).split('?')[0];

        return cleanImageUrl === cleanVariantImageUrl;
      });

      return matchingImage
        ? matchingImage.closest('[data-media-id]')
        : null;
    }

    escapeSelector(value) {
      if (window.CSS && typeof window.CSS.escape === 'function') {
        return window.CSS.escape(String(value));
      }

      return String(value).replace(
        /[^a-zA-Z0-9_-]/g,
        '\\$&'
      );
    }

    applySelectedVariant() {
      const variant = this.getMatchingVariant();
      const previewVariant = this.getPreviewVariant();

      if (!variant) {
        if (previewVariant) {
          this.applyPreviewMedia(previewVariant);
        }

        this.updateStatus();
        return;
      }

      if (!variant.selectable) {
        this.setStatus(
          'This configuration is currently unavailable.',
          'error'
        );
        return;
      }

      this.applyPreviewMedia(previewVariant || variant);

      /*
        The final purchasable variant is synced silently so choosing
        installation does not replace the gallery with a variant that
        has no image assigned. Installation changes the price and the
        cart variant only. The pergola image remains controlled by the
        first four configuration choices.
      */

      this.syncMobileVariantForms(variant);
      this.updateMobilePreview(previewVariant || variant);
      this.updateDisplayedPrice(variant);

      this.setStatus(
        'Your configuration has been selected. Pricing and purchase options will update automatically.',
        'valid'
      );
    }

    updateMobilePreview(variant) {
      const preview = this.root.querySelector(
        '[data-configurator-mobile-preview]'
      );

      const image = this.root.querySelector(
        '[data-configurator-mobile-preview-image]'
      );

      if (!preview || !image) return;

      const isMobile = window.matchMedia(
        '(max-width: 749px)'
      ).matches;

      if (
        !isMobile ||
        !variant ||
        !variant.imageUrl
      ) {
        preview.hidden = true;
        image.removeAttribute('src');
        image.alt = '';
        return;
      }

      image.src = variant.imageUrl;
      image.alt = variant.title
        ? `${variant.title} pergola`
        : 'Your selected pergola';

      preview.hidden = false;
    }

    syncMobileVariantForms(variant) {
      const productInfo = this.root.closest('product-info');

      if (!productInfo) return;

      productInfo
        .querySelectorAll('form input[name="id"]')
        .forEach((input) => {
          input.value = String(variant.id);
          input.setAttribute('value', String(variant.id));
        });

      const nativePicker = this.root.querySelector(
        '[data-native-variant-picker]'
      );

      if (nativePicker) {
        const fieldsets = Array.from(
          nativePicker.querySelectorAll('fieldset')
        );

        const selects = Array.from(
          nativePicker.querySelectorAll('select')
        );

        variant.options.forEach((optionValue, optionIndex) => {
          const fieldset = fieldsets[optionIndex];

          if (fieldset) {
            fieldset
              .querySelectorAll('input[type="radio"]')
              .forEach((radio) => {
                radio.checked =
                  String(radio.value).trim() ===
                  String(optionValue).trim();
              });

            return;
          }

          const select = selects[optionIndex];

          if (select) {
            select.value = optionValue;
          }
        });
      }

      const productUrl =
        this.root.dataset.productUrl ||
        window.location.pathname;

      const updatedUrl = new URL(
        productUrl,
        window.location.origin
      );

      updatedUrl.searchParams.set(
        'variant',
        String(variant.id)
      );

      window.history.replaceState(
        {},
        '',
        `${updatedUrl.pathname}${updatedUrl.search}${updatedUrl.hash}`
      );

      productInfo
        .querySelectorAll(
          '.product-form__submit, button[name="add"]'
        )
        .forEach((button) => {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        });
    }

    syncNativeVariantPicker(variant) {
      const nativePicker = this.root.querySelector(
        '[data-native-variant-picker]'
      );

      if (!nativePicker) return;

      const fieldsets = Array.from(
        nativePicker.querySelectorAll('fieldset')
      );

      const selects = Array.from(
        nativePicker.querySelectorAll('select')
      );

      let finalControl = null;

      variant.options.forEach((optionValue, optionIndex) => {
        const fieldset = fieldsets[optionIndex];

        if (fieldset) {
          const radio = Array.from(
            fieldset.querySelectorAll('input[type="radio"]')
          ).find(
            (input) =>
              String(input.value).trim() ===
              String(optionValue).trim()
          );

          if (radio) {
            radio.checked = true;
            finalControl = radio;
          }

          return;
        }

        const select = selects[optionIndex];

        if (select) {
          select.value = optionValue;
          finalControl = select;
        }
      });

      const productInfo = this.root.closest('product-info');

      if (productInfo) {
        productInfo
          .querySelectorAll('form input[name="id"]')
          .forEach((input) => {
            input.value = variant.id;
            input.setAttribute('value', variant.id);

            input.dispatchEvent(
              new Event('change', {
                bubbles: true
              })
            );
          });
      }

      if (finalControl) {
        finalControl.dispatchEvent(
          new Event('input', {
            bubbles: true
          })
        );

        finalControl.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      }
    }

    formatMoney(amountInMinorUnits) {
      const amount = Number(amountInMinorUnits) / 100;

      if (!Number.isFinite(amount)) {
        return '';
      }

      const formatted = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: this.currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);

      return `${formatted} ${this.currencyCode}`;
    }

    updateDisplayedPrice(variant) {
      if (!variant) return;

      const productInfo = this.root.closest('product-info');

      if (!productInfo) return;

      const sectionId = this.root.dataset.sectionId;

      const priceContainer =
        productInfo.querySelector(`#price-${sectionId}`) ||
        productInfo.querySelector('[id^="price-"]');

      if (!priceContainer) return;

      const currentPrice = this.formatMoney(variant.price);

      const compareAtPrice = Number(variant.compareAtPrice);
      const variantPrice = Number(variant.price);

      const isOnSale =
        Number.isFinite(compareAtPrice) &&
        compareAtPrice > variantPrice;

      const priceWrapper = priceContainer.querySelector('.price');

      if (priceWrapper) {
        priceWrapper.classList.toggle(
          'price--on-sale',
          isOnSale
        );
      }

      const regularPriceElements =
        priceContainer.querySelectorAll(
          '.price-item--regular'
        );

      const salePriceElements =
        priceContainer.querySelectorAll(
          '.price-item--sale'
        );

      regularPriceElements.forEach((element) => {
        element.textContent = isOnSale
          ? this.formatMoney(compareAtPrice)
          : currentPrice;
      });

      salePriceElements.forEach((element) => {
        element.textContent = currentPrice;
      });

      priceContainer.dispatchEvent(
        new CustomEvent('pergola:price-updated', {
          bubbles: true,
          detail: {
            variantId: variant.id,
            price: variant.price
          }
        })
      );
    }

    updateStatus() {
      const complete = STEP_ORDER.every(
        (step) => this.selection[step]
      );

      if (!complete) {
        const nextStep = STEP_ORDER.find(
          (step) => !this.selection[step]
        );

        const stepLabels = {
          finish: 'finish',
          size: 'dimensions',
          legs: 'frame layout',
          installation: 'installation option'
        };

        this.setStatus(
          `Choose your ${stepLabels[nextStep]} to continue.`,
          ''
        );

        return;
      }

      const variant = this.getMatchingVariant();

      if (!variant) {
        this.setStatus(
          'This combination is not currently available. Please adjust your selections.',
          'error'
        );

        return;
      }

      if (!variant.selectable) {
        this.setStatus(
          'This configuration is currently unavailable.',
          'error'
        );

        return;
      }

      this.setStatus(
        'Your pergola configuration is ready.',
        'valid'
      );
    }

    setStatus(message, state) {
      if (!this.statusElement) return;

      this.statusElement.textContent = message;
      this.statusElement.classList.remove(
        'is-valid',
        'is-error'
      );

      if (state === 'valid') {
        this.statusElement.classList.add('is-valid');
      }

      if (state === 'error') {
        this.statusElement.classList.add('is-error');
      }
    }
  }

  const initialiseConfigurators = (scope = document) => {
    scope
      .querySelectorAll(CONFIGURATOR_SELECTOR)
      .forEach((root) => {
        if (root.dataset.pergolaInitialised !== 'true') {
          new PergolaConfigurator(root);
        }
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseConfigurators();
    });
  } else {
    initialiseConfigurators();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseConfigurators(event.target);
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches(CONFIGURATOR_SELECTOR)) {
          initialiseConfigurators(node.parentElement || document);
          return;
        }

        if (node.querySelector(CONFIGURATOR_SELECTOR)) {
          initialiseConfigurators(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();

(() => {
  const CAROUSEL_SELECTOR =
    '.james-product-theme .product__media-wrapper .thumbnail-slider';

  const setupVerticalThumbnailCarousel = (slider) => {
    if (
      !slider ||
      slider.dataset.verticalCarouselReady === 'true'
    ) {
      return;
    }

    const list = slider.querySelector('.thumbnail-list');
    const originalPreviousButton = slider.querySelector(
      '.slider-button--prev'
    );
    const originalNextButton = slider.querySelector(
      '.slider-button--next'
    );

    if (
      !list ||
      !originalPreviousButton ||
      !originalNextButton
    ) {
      return;
    }

    const previousButton =
      originalPreviousButton.cloneNode(true);

    const nextButton =
      originalNextButton.cloneNode(true);

    originalPreviousButton.replaceWith(previousButton);
    originalNextButton.replaceWith(nextButton);

    slider.dataset.verticalCarouselReady = 'true';

    const getScrollAmount = () => {
      const firstThumbnail = list.querySelector(
        '.thumbnail-list__item'
      );

      if (!firstThumbnail) {
        return 100;
      }

      const listStyles = window.getComputedStyle(list);

      const gap =
        parseFloat(
          listStyles.rowGap ||
          listStyles.gap ||
          '0'
        ) || 0;

      return (
        firstThumbnail.getBoundingClientRect().height + gap
      );
    };

    const updateButtons = () => {
      const clientHeight = list.clientHeight;
      const scrollHeight = list.scrollHeight;

      if (clientHeight < 20 || scrollHeight < 20) {
        return;
      }

      const maximumScroll = Math.max(
        0,
        scrollHeight - clientHeight
      );

      previousButton.disabled = list.scrollTop <= 2;

      nextButton.disabled =
        maximumScroll <= 2 ||
        list.scrollTop >= maximumScroll - 2;
    };

    const scheduleButtonUpdate = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(updateButtons);
      });
    };

    const scrollThumbnails = (direction) => {
      list.scrollBy({
        top: getScrollAmount() * direction,
        behavior: 'smooth'
      });

      window.setTimeout(updateButtons, 350);
    };

    previousButton.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        scrollThumbnails(-1);
      },
      true
    );

    nextButton.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        scrollThumbnails(1);
      },
      true
    );

    list.addEventListener(
      'scroll',
      scheduleButtonUpdate,
      {
        passive: true
      }
    );

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(
        scheduleButtonUpdate
      );

      resizeObserver.observe(slider);
      resizeObserver.observe(list);
    }

    const mutationObserver = new MutationObserver(
      scheduleButtonUpdate
    );

    mutationObserver.observe(list, {
      childList: true,
      subtree: true
    });

    slider
      .querySelectorAll('img')
      .forEach((image) => {
        if (!image.complete) {
          image.addEventListener(
            'load',
            scheduleButtonUpdate,
            { once: true }
          );
        }
      });

    window.addEventListener(
      'resize',
      scheduleButtonUpdate
    );

    window.addEventListener(
      'load',
      scheduleButtonUpdate,
      { once: true }
    );

    previousButton.disabled = true;
    nextButton.disabled = false;

    scheduleButtonUpdate();

    window.setTimeout(scheduleButtonUpdate, 150);
    window.setTimeout(scheduleButtonUpdate, 500);
    window.setTimeout(scheduleButtonUpdate, 1000);
  };

  const initialiseVerticalThumbnailCarousels = (
    scope = document
  ) => {
    if (
      scope instanceof Element &&
      scope.matches(CAROUSEL_SELECTOR)
    ) {
      setupVerticalThumbnailCarousel(scope);
    }

    scope
      .querySelectorAll(CAROUSEL_SELECTOR)
      .forEach(setupVerticalThumbnailCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initialiseVerticalThumbnailCarousels();
      }
    );
  } else {
    initialiseVerticalThumbnailCarousels();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialiseVerticalThumbnailCarousels(
        event.target
      );
    }
  );

  const pageObserver = new MutationObserver(
    (mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          initialiseVerticalThumbnailCarousels(node);
        });
      });
    }
  );

  pageObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();

(() => {
  const THUMBNAIL_SELECTOR =
    '.james-product-theme .product__media-wrapper .thumbnail-slider';

  const synchroniseThumbnailHeight = (thumbnailSlider) => {
    if (
      !thumbnailSlider ||
      thumbnailSlider.dataset.galleryHeightSync === 'true'
    ) {
      return;
    }

    const mediaGallery = thumbnailSlider.closest('media-gallery');

    if (!mediaGallery) return;

    const mainGallery = mediaGallery.querySelector(
      'slider-component:not(.thumbnail-slider)'
    );

    if (!mainGallery) return;

    thumbnailSlider.dataset.galleryHeightSync = 'true';

    const updateHeight = () => {
      window.requestAnimationFrame(() => {
        const mainGalleryHeight =
          mainGallery.getBoundingClientRect().height;

        if (mainGalleryHeight < 100) return;

        thumbnailSlider.style.setProperty(
          '--pergola-gallery-height',
          `${Math.round(mainGalleryHeight)}px`
        );

        const thumbnailList =
          thumbnailSlider.querySelector('.thumbnail-list');

        if (thumbnailList) {
          thumbnailList.dispatchEvent(
            new Event('scroll', {
              bubbles: false
            })
          );
        }
      });
    };

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(updateHeight);

      resizeObserver.observe(mainGallery);
    }

    mainGallery.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', updateHeight, {
          once: true
        });
      }
    });

    window.addEventListener('resize', updateHeight);
    window.addEventListener('load', updateHeight, {
      once: true
    });

    updateHeight();
    window.setTimeout(updateHeight, 200);
    window.setTimeout(updateHeight, 700);
  };

  const initialiseGalleryHeightSync = (scope = document) => {
    if (
      scope instanceof Element &&
      scope.matches(THUMBNAIL_SELECTOR)
    ) {
      synchroniseThumbnailHeight(scope);
    }

    scope
      .querySelectorAll(THUMBNAIL_SELECTOR)
      .forEach(synchroniseThumbnailHeight);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseGalleryHeightSync();
    });
  } else {
    initialiseGalleryHeightSync();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseGalleryHeightSync(event.target);
  });
})();